<template>
  <section class="overview-submenu">
    <div class="overview-submenu__left">
      <div class="overview-submenu__title">Standard Event Stats</div>
      <!-- PDF and other reports feature coming soon but not ready yet -->
      <!-- <Button outlined :disabled="true">
        <IconReport />
        Reports
      </Button>
      <ComingSoon /> -->
    </div>
    <div class="overview-submenu__right" v-if="!editMode">
      <div class="benchmark-controls">
        <FeatureFlag name="basic_benchmarking_widgets">
          <ToggleSwitch v-model="analyticsStore.isBenchmarking" />
          <div class="benchmark-label">
            <span>Activate Benchmarking</span>
            <Tooltip>
              <template v-slot:text>
                <IconInfo />
              </template>
              <template v-slot:tooltip>
                Activate Benchmarking to see the benchmarking data for your event.
              </template>
            </Tooltip>
          </div>
        </FeatureFlag>
      </div>

      <Button
        outlined
        aria-label="Copy UUID"
        v-if="analyticsStore.chosenEvent && userStore.isMeshhAdmin"
        @click="copyToClipboard(toast, analyticsStore.chosenEvent.uuid)"
      >
        <IconCopy /> Copy UUID
      </Button>
      <Tooltip v-if="!editMode" position="left">
        <template v-slot:text>
          <Button
            outlined
            @click="startEditMode"
            :disabled="analyticsStore.isDemoEventSelected && userStore.isNonStaff"
          >
            <IconEdit />
          </Button>
        </template>
        <template v-slot:tooltip>
          <template v-if="analyticsStore.isDemoEventSelected && userStore.isNonStaff">
            You do not have permissions to edit the layout for this event
          </template>
          <template v-else> Edit metrics </template>
        </template>
      </Tooltip>
    </div>
    <div class="overview-submenu__right" v-else>
      <Button v-if="editMode" @click="cancelChanges" outlined> Cancel Changes </Button>
      <Button v-if="editMode" @click="saveTemplate">
        <IconSave />
        Save Changes
      </Button>
      <MetricAdd
        v-if="editMode"
        v-model:widgets="analyticsStore.chosenWidgets"
        @onAddedWidget="onAddedWidget"
      />
    </div>
  </section>

  <div class="widget-container" data-step="3" :class="{ 'widgets--edit': editMode }">
    <div class="grid-stack">
      <div
        v-for="widgetItem in analyticsStore.chosenWidgets"
        class="grid-stack-item"
        :gs-x="widgetItem.x"
        :gs-y="widgetItem.y"
        :gs-w="widgetItem.w"
        :gs-h="widgetItem.h"
        :gs-id="widgetItem.id"
        :id="`widget-${widgetItem.id}`"
        :key="widgetItem.id"
      >
        <WidgetBase
          :widget="widgetItem.widget"
          :editMode="editMode"
          @onWidgetRemove="onWidgetRemove"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';

import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/gridstack-extra.min.css';
import { GridStack } from 'gridstack';

import { Button } from 'primevue';
import { ToggleSwitch } from 'primevue';

import ComingSoon from '@/components/global/ComingSoon.vue';
import Tooltip from '@/components/global/Tooltip.vue';
import MetricAdd from '@/components/dialogs/MetricAdd.vue';
import WidgetBase from '@/components/widgets/WidgetBase.vue';

import { updateEventTemplate } from '@/utils/api';
import { copyToClipboard } from '@/utils/clipboardCopy';

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

import { useAnalyticsStore } from '@/stores/analytics';
const analyticsStore = useAnalyticsStore();

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useToastStore } from '@/utils/toast';
const toast = useToastStore();

import IconCopy from '@/icons/events/IconCopy.svg?component';
import IconReport from '@/icons/analytics/IconReport.svg?component';
import IconSave from '@/icons/analytics/IconSave.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import FeatureFlag from '@/components/global/FeatureFlag.vue';

const editMode = ref(false);

let grid: GridStack | null = null;

const setupGrid = () => {
  nextTick(() => {
    grid = GridStack.init({
      alwaysShowResizeHandle: true,
      column: 3,
      cellHeight: 275,
      margin: 8,
      float: false,
      handle: '.widget-move',
      disableResize: true,
      disableDrag: true,
    });

    grid.on('dragstop', function (event, element) {
      const node = element.gridstackNode;
      // TypeScript requires explicit null checks for optional properties
      if (node && node.id && node.x && node.y && node.w && node.h) {
        updateWidgetPlacement(node.id, node.x, node.y, node.w, node.h);
      }
    });

    grid.on('resizestop', function (event, element) {
      const node = element.gridstackNode;
      // This doesn't seem to fire but I have no idea if that matters? Maybe not?
      if (node && node.id && node.x && node.y && node.w && node.h) {
        updateWidgetPlacement(node.id, node.x, node.y, node.w, node.h);
      }
    });
  });
};

eventsStore.getEventListData();
setupGrid();

const startEditMode = () => {
  editMode.value = true;
  if (grid) {
    grid.opts.disableDrag = false;
    grid.opts.disableResize = false;
    grid.resizable('.grid-stack-item', true);
  }
};

const prepWidgets = () => {
  // Update the position, size, and order for all widgets.
  const domWidgets = grid?.engine.nodes;

  if (domWidgets && domWidgets.length) {
    const widgetsToSave = domWidgets.map((widget) => {
      return {
        id: widget.id,
        widget_id: widget.id,
        position: {
          h: widget.h,
          w: widget.w,
          x: widget.x,
          y: widget.y,
        },
      };
    });

    return widgetsToSave;
  }

  return [];
};

const saveTemplate = () => {
  const widgetsToSave = prepWidgets();
  const eventId = analyticsStore.chosenEvent?.id;
  const templateId = analyticsStore.chosenEventDetails?.template_id;

  const payload = {
    widgets: widgetsToSave,
  };

  if (eventId && templateId) {
    updateEventTemplate(userStore.accessToken, eventId, templateId, payload)
      .then((resp) => {
        if (toast.success(resp)) {
          editMode.value = false;
          if (grid) {
            grid.opts.disableResize = true;
            grid.opts.disableDrag = true;
            grid.resizable('.grid-stack-item', false);
          }
          analyticsStore.chosenEventDetails.template_id = resp.data.id;
        }
      })
      .catch((error) => {
        toast.error(error);
      });
  }
};

const cancelChanges = async () => {
  grid?.removeAll(true);
  await analyticsStore.getEventWidgets();
  setupGrid();
  editMode.value = false;
};

// Update the Widget properties to match the new size/placement for when we save it.
const updateWidgetPlacement = (id: string, x: number, y: number, w: number, h: number) => {
  const widget = analyticsStore.chosenWidgets.find((widget) => widget.id === parseInt(id));
  if (widget) {
    widget.x = x;
    widget.y = y;
    widget.h = h;
    widget.w = w;
    widget.widget.position.x = x;
    widget.widget.position.y = y;
    widget.widget.position.w = w;
    widget.widget.position.h = h;
  }
};

const onWidgetRemove = (widgetId: number) => {
  if (grid) {
    const selector = `#widget-${widgetId}`;
    grid.removeWidget(selector, false);

    analyticsStore.chosenWidgets = analyticsStore.chosenWidgets.filter((widget) => {
      return widget.id !== widgetId;
    });
  }
};

const onAddedWidget = (newWidgetId: number) => {
  if (grid) {
    grid.makeWidget(`#widget-${newWidgetId}`);
  }
};
</script>

<style lang="scss" scoped>
.benchmark-controls {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-right: 10px;
}

.benchmark-label {
  align-items: center;
  display: flex;
  gap: 8px;

  .premium-icon {
    height: 16px;
    fill: variables.$vantage-base-primitives-color-secondary-500;
    width: 16px;
  }
}
</style>
